import { forwardRef, useRef } from 'react';
import styles from '../Inputs.module.scss.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { combineRefs } from '../../util/combineRefs.js';
import classNames from 'clsx';

const NumberInput = /*#__PURE__*/forwardRef(function NumberInput(props, forwardedRef) {
  const {
    align = 'start',
    autoComplete = 'off',
    className: callerClassName,
    disabled,
    name,
    precision,
    readOnly,
    step = 1,
    min,
    max,
    tag = name,
    unit,
    value,
    variant = 'default',
    onChange,
    UNSAFE_active,
    UNSAFE_focus,
    UNSAFE_hover,
    ...inputProps
  } = props;
  const minNum = Number(min ?? Number.NEGATIVE_INFINITY);
  const maxNum = Number(max ?? Number.POSITIVE_INFINITY);
  const stepNum = Number(step);
  const ref = useRef(null);
  function handleChange(e) {
    const ev = e.currentTarget.value;
    const next = parseValue(ev, precision);
    onChange?.(next);
  }
  function handleKeyDown(e) {
    if (['e', 'E', '+'].includes(e.key)) {
      e.preventDefault();
      return;
    }
    const ev = e.currentTarget.value;
    const value = parseValue(ev, precision);
    const valueNum = Number(value);
    if (e.key === 'ArrowUp') {
      const next = clamp(valueNum + stepNum, minNum, maxNum);
      onChange?.(next.toString());
    }
    if (e.key === 'ArrowDown') {
      const next = clamp(valueNum - stepNum, minNum, maxNum);
      onChange?.(next.toString());
    }
  }
  function handlePaste(e) {
    const data = e.clipboardData.getData('Text');
    const next = parseValue(data, precision);
    onChange?.(next);
  }
  function handleClick() {
    ref.current?.focus();
  }
  function handleMouseDown(e) {
    if (e.target !== ref.current) {
      e.preventDefault();
    }
  }
  const containerClassName = classNames('dsx-component', styles['input-box'], styles['container'], styles['cursor-text'], styles[variant], readOnly && styles['read-only'], UNSAFE_hover && styles['hover'], UNSAFE_active && styles['active'], UNSAFE_focus && styles['focus'], callerClassName);
  const inputClassName = classNames(styles['input'], styles['tabular-nums'], styles[align]);

  // Q: Why use a text input instead of a number, here?
  // A: https://stackoverflow.blog/2022/12/26/why-the-number-input-is-the-worst-input/

  return /*#__PURE__*/jsxs("fieldset", {
    className: containerClassName,
    disabled: disabled,
    onClick: handleClick,
    onMouseDown: handleMouseDown,
    children: [/*#__PURE__*/jsx("input", {
      ...inputProps,
      type: "text",
      inputMode: "decimal",
      value: value,
      name: name,
      className: inputClassName,
      autoComplete: autoComplete,
      readOnly: readOnly,
      ref: combineRefs(forwardedRef, ref),
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onPaste: handlePaste,
      "data-dsx--number-input": tag
    }), unit != null && unit]
  });
});
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function parseValue(original, precision) {
  const nan = isNaN(Number(original)) || isNaN(parseFloat(original));

  // if it's not a number, grab the numeric parts and go with that.
  if (nan) {
    const numRegex = /(\d|-|\.)/g;
    const next = original.match(numRegex)?.join('') || '';
    return next;
  }

  // otherwise cut it down to the correct precision
  let regex = /(-)?(\d+)?(\.(\d*)?)?/g;
  if (precision === 0) {
    regex = /(-)?(\d+)?/g;
  }
  if (precision && precision > 0) {
    regex = new RegExp(`(-)?(\\d+)?(\\.(\\d{0,${precision}})?)?`, 'g');
  }
  const text = original.match(regex)?.[0] || '';
  return text;
}

export { NumberInput };
